import PersonOutlined from "@mui/icons-material/PersonOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import ManageAccountsOutlined from "@mui/icons-material/ManageAccountsOutlined";
import AssessmentOutlined from "@mui/icons-material/AssessmentOutlined";
import TrackChangesOutlined from "@mui/icons-material/TrackChangesOutlined";
import MailOutlined from "@mui/icons-material/MailOutlined";
import { HealthTargetMetric } from "./GoalsSection/types";
import { FAQItem, SettingsNavItem, SettingsSectionId } from "./types";

export const SETTINGS_BASE_PATH = "/settings";
export const GOALS_SETTINGS_PATH = `${SETTINGS_BASE_PATH}/goals`;
export const HEALTH_TARGET_PARAM = "target";
export const healthTargetPath = (metric: HealthTargetMetric) =>
  `${GOALS_SETTINGS_PATH}?${HEALTH_TARGET_PARAM}=${metric}`;
export const GOALS_VIEW_PARAM = "view";
export const ROLL_FORWARD_PARAM = "rollForward";
export const pastGoalsPath = `${GOALS_SETTINGS_PATH}?${GOALS_VIEW_PARAM}=past`;
export const rollForwardPath = (goalId: number) =>
  `${GOALS_SETTINGS_PATH}?${ROLL_FORWARD_PARAM}=${goalId}`;

export const DEFAULT_SECTION: SettingsSectionId = "profile";

export const SETTINGS_TITLE = "Settings";
export const SETTINGS_SUBTITLE =
  "Manage your account preferences and security settings";

export const SETTINGS_SECTIONS: SettingsNavItem[] = [
  {
    id: "profile",
    label: "Profile",
    subtitle: "Personal details",
    icon: PersonOutlined,
  },
  {
    id: "security",
    label: "Security",
    subtitle: "Password & access",
    icon: LockOutlined,
  },
  {
    id: "preferences",
    label: "Preferences",
    subtitle: "Notifications & appearance",
    icon: TuneOutlined,
  },
  {
    id: "goals",
    label: "Goals",
    subtitle: "Targets & plan",
    icon: TrackChangesOutlined,
  },
  {
    id: "reports",
    label: "Reports",
    subtitle: "Custom & tax-year reports",
    icon: AssessmentOutlined,
  },
  {
    id: "account",
    label: "Account",
    subtitle: "Export & deletion",
    icon: ManageAccountsOutlined,
  },
  {
    id: "contact",
    label: "Contact",
    subtitle: "Help & support",
    icon: MailOutlined,
  },
];

// These sections read none of the profile/preferences payload, so they skip its loading and error gates.
export const SECTIONS_WITHOUT_USER_DATA: readonly SettingsSectionId[] = [
  "goals",
  "reports",
];

export const isSettingsSection = (
  value: string | undefined,
): value is SettingsSectionId =>
  SETTINGS_SECTIONS.some((section) => section.id === value);

/**
 * FAQ Data for the Contact/Support page
 * Organized by category for easier maintenance
 */
export const FAQ_DATA: FAQItem[] = [
  {
    id: "reset-password",
    question: "How do I reset my password?",
    answer:
      "Navigate to the Security tab in Settings and click on 'Change Password'. You'll need to enter your current password and then your new password twice to confirm.",
    category: "Security",
  },
  {
    id: "export-data",
    question: "How do I export my data?",
    answer:
      "Go to the Account tab in Settings and scroll to the 'Data Management' section. Click on 'Export Data' and you'll receive a download of all your financial information in CSV format.",
    category: "Account",
  },
  {
    id: "delete-account",
    question: "How do I delete my account?",
    answer:
      "In the Account tab under Settings, scroll to the bottom to find 'Account Actions'. Click on 'Delete Account' and follow the confirmation prompts. Please note that this action is permanent and cannot be undone.",
    category: "Account",
  },
  {
    id: "data-security",
    question: "Is my financial data secure?",
    answer:
      "Yes! We use industry-standard encryption (AES-256) to protect your data both in transit and at rest. We never share your personal financial information with third parties, and our servers are hosted in secure data centers with regular security audits.",
    category: "Security",
  },
  {
    id: "email-preferences",
    question: "How do I change my email preferences?",
    answer:
      "Visit the Preferences tab in Settings and toggle 'Email Notifications' on or off based on your preference. You'll still receive critical security alerts even if you disable general notifications.",
    category: "Preferences",
  },
];

/**
 * Subject options for the contact form dropdown
 */
export const SUBJECT_OPTIONS = [
  { label: "Bug Report", value: "bug-report" },
  { label: "Feature Request", value: "feature-request" },
  { label: "Account Issue", value: "account-issue" },
  { label: "General Question", value: "general-question" },
  { label: "Other", value: "other" },
] as const;

export const SUPPORT_EMAIL =
  import.meta.env.VITE_SUPPORT_EMAIL || "my.ntw.app@gmail.com";
